import { type ReactNode, useMemo, useEffect, Fragment } from 'react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as Yup from 'yup'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
// import IconButton from '@mui/material/IconButton'
import Input from 'components/Input'
import RequestStatus from 'components/RequestStatus/RequestStatus'
import { useTranslation } from 'react-i18next'
import type { PrizeEditFieldsType, EditPrizeRequestBody, Period } from './PrizeEditForm.types'
import type { ApiLanguage } from 'pages/Markets/Form/MarketForm.types'
import type { PrizeGroup } from 'types/Prize'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { axiosPost } from 'connectors/axiosPost'
import { excelExt } from 'config/extensions'
import FileUpload from 'components/FileUpload'
// import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined'
import { Divider, MenuItem, Typography } from '@mui/material'
import { useAlert } from 'context/AlertContext'
import useQueryGet from 'hooks/useQueryGet'
import { useAuthContext } from 'context/AuthContext/AuthContext'
import { ChevronLeft } from '@mui/icons-material'
import Select from 'components/Select'
import { Market } from 'types/Market'
import { useAppContext } from 'context/AppContext'
import { PeriodType, PrizeType } from '../AddForm/PrizeAddForm.types'
import PhysicalFields from '../AddForm/PhysicalFields'
import PageLoader from 'components/PageLoader'

const PrizeEditFields = ({ prizeData }: { prizeData: PrizeGroup }): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { changeMessage } = useAlert()
  const { token, refreshToken } = useAuthContext()
  const { markets } = useAppContext()

  const { data: languages, isLoading: isLanguagesLoading } = useQueryGet<ApiLanguage[]>({
    queryKey: ['markets-languages', 'prize-translations-langs', prizeData, `prize-${prizeData.id}`],
    endpoint: `market-languages/${prizeData.marketId}`,
  })

  const {
    mutate: editPrize,
    data: responseData,
    reset,
    isLoading,
  } = useMutation(
    async (prize: FormData) => await axiosPost<PrizeGroup>(token, refreshToken, `prizes/update/${prizeData.id}`, prize),
    {
      onSuccess: data => {
        if (data !== undefined && !('error' in data)) {
          navigate('/prizes-groups')
        }
      },
    }
  )

  const prizeTypeMock: PrizeType[] = [
    {
      label: t('prize.physical'),
      value: 'physical',
    },
    {
      label: t('prize.files'),
      value: 'files',
    },
    {
      label: t('prize.digitalCodes'),
      value: 'digital-codes',
    },
    {
      label: t('prize.digitalLinks'),
      value: 'digital-links',
    },
  ]

  const periodsMock: PeriodType[] = [
    {
      label: t('prize.periodDay'),
      value: 'day',
    },
    {
      label: t('prize.workingDay'),
      value: 'working-day',
    },
    {
      label: t('prize.periodWeek'),
      value: 'week',
    },
    {
      label: t('prize.periodMonth'),
      value: 'month',
    },
    {
      label: t('prize.periodWhole'),
      value: 'whole-promo',
    },
  ]

  useEffect(() => {
    if (responseData !== undefined && 'error' in responseData && responseData?.error?.data.message !== undefined) {
      changeMessage(responseData.error.data.message, 'error', reset)
    }
  }, [responseData])

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required(t('forms.required', { field: t('common.name') }) ?? 'Required')
      .matches(/^[A-Za-z_-]+$/, t('forms.fieldSlug') ?? 'only [A-Za-z_-]'),
    file: Yup.mixed().nullable(),
    translations: Yup.lazy(value => {
      if (value !== undefined) {
        const newEntries = Object.keys(value).reduce(
          (acc, key) => ({
            ...acc,
            [key]: Yup.string().required(),
          }),
          {}
        )
        return Yup.object().shape(newEntries).required()
      }
      return Yup.mixed().notRequired()
    }),
    periods: Yup.array()
      .of(
        Yup.object().shape({
          startDate: Yup.date(),
          endDate: Yup.date(),
          amount: Yup.string(),
        })
      )
      .test('availablePrizes', t('forms.prizesAmount') ?? '', (item, { parent }) => {
        const amountLeft = prizeData.amountLeft
        if (parent.period === 'day' || parent.period === 'working-day' || parent.period === 'whole-promo') {
          return true
        }

        if (prizeData.market.flow === 'pd') {
          return true
        }

        if (amountLeft !== undefined) {
          const sumOfPrizes = item?.reduce((a, b) => a + Number(b.amount), 0)

          return Number(parent.totalAmount) === sumOfPrizes
        }

        return false
      }),
  })

  const percent = useMemo(() => {
    const left = prizeData.amountLeft
    const total = prizeData.totalAmount
    if (left / total <= 0.1) {
      return '10%'
    } else if (left / total <= 0.3) {
      return '30%'
    } else if (left / total <= 0.5) {
      return '50%'
    } else return ''
  }, [prizeData])

  const defaultValues: PrizeEditFieldsType = {
    name: prizeData.name,
    market: prizeData.market,
    totalAmount: prizeData.totalAmount,
    rewardsPerIssue: prizeData.rewardsPerIssue.toString(),
    file: null,
    period: prizeData.period,
    type: prizeData.type,
    periods: prizeData.ranges.map<Period>(period => ({
      id: period.id.toString(),
      used: period.amount - period.amountLeft,
      api: true,
      startDate: new Date(period.startDate),
      endDate: new Date(period.endDate),
      amount: period.amount.toString(),
      amountLeft: period.amountLeft.toString(),
    })),
    descriptions: Object.fromEntries(prizeData.translations.map(el => [el.marketLanguageId, el.description])),
    translations: Object.fromEntries(prizeData.translations.map(el => [el.marketLanguageId, el.translation])),
  }

  const { handleSubmit, control, watch, setValue, getValues } = useForm<PrizeEditFieldsType>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  // const { fields: periods, append, remove } = useFieldArray<PrizeEditFieldsType>({ name: 'periods', control })

  const translations = useMemo((): ReactNode => {
    if (languages === undefined || 'error' in languages) {
      return <RequestStatus data={languages} isLoading={isLanguagesLoading} />
    }

    return (
      <Box display="flex" flexDirection="column" gap={2}>
        {languages.map(lang => (
          <Fragment key={lang.id}>
            <Input<PrizeEditFieldsType>
              name={`translations.${lang.id}`}
              label={`${t('common.translation')} - ${lang.name} | ${lang.nativeName}`}
              control={control}
              textInputProps={{ sx: { mt: 1 } }}
            />
            <Input<PrizeEditFieldsType>
              name={`descriptions.${lang.id}`}
              textInputProps={{ multiline: true, rows: 5 }}
              label={`${t('common.description')} - ${lang.name} | ${lang.nativeName}`}
              control={control}
            />
          </Fragment>
        ))}
      </Box>
    )
  }, [languages])

  const typeFields = useMemo(() => {
    const market = watch('market')
    if (market === null || market.flow === 'pd') {
      return null
    }

    return (
      <PhysicalFields
        market={market}
        period={watch('period')}
        prizesTotal={watch('totalAmount')}
        control={control}
        setValue={setValue}
        getValues={getValues}
      />
    )
  }, [watch('type'), watch('period'), watch('periods'), watch('market'), watch('totalAmount')])

  const onHandleSubmit = async (data: PrizeEditFieldsType): Promise<any> => {
    const fd = new FormData()
    const newTranslations = Object.fromEntries(
      prizeData.translations.map(translation => [translation.id, data.translations[translation.marketLanguageId]])
    )
    const newDescriptions = Object.fromEntries(
      prizeData.translations.map(translation => [translation.id, data.descriptions[translation.marketLanguageId]])
    )
    const requestBody: EditPrizeRequestBody = {
      ...data,
      translations: newTranslations,
      descriptions: newDescriptions,
      id: prizeData.id,
      rewardsPerIssue: Number(data.rewardsPerIssue),
      periods: data.periods.map(({ used, api, ...e }) => ({ ...e, amountLeft: (+e.amount - (used || 0)).toString() })),
    }

    Object.entries(requestBody).forEach(([key, value]) => {
      if (key === 'used' || key === 'api') {
        return
      }
      if (typeof value === 'string' || value instanceof File) {
        fd.append(key, value)
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        fd.append(key, value.toString())
      } else if (value instanceof Object && !(value instanceof File)) {
        fd.append(key, JSON.stringify(value))
      } else if (value !== null && value !== undefined) {
        fd.append(key, JSON.stringify(value))
      }
    })

    editPrize(fd)
  }

  return (
    <form onSubmit={handleSubmit(onHandleSubmit)} noValidate>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={() => {
            navigate('/prizes-groups')
          }}
          startIcon={<ChevronLeft />}
        >
          {t('common.backListText')}
        </Button>
        <Button type="submit" variant="contained">
          {t('common.change')}
        </Button>
      </Box>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Input<PrizeEditFieldsType> name="name" label={t('common.name')} control={control} />
        <Input<PrizeEditFieldsType>
          name="rewardsPerIssue"
          textInputProps={{ type: 'number' }}
          label={t('common.rewardsPerIssue')}
          control={control}
        />
        <Box>
          <Select<Market, PrizeEditFieldsType>
            control={control}
            name="market"
            withText
            disabled
            label={t('common.marketName')}
            elements={markets?.data || []}
            getOptionLabel={market => market.name}
            withTextProps={{
              isOptionEqualToValue: (option, value) => {
                return option.id === value.id
              },
            }}
            renderOption={(props, market) => (
              <Box component="li" {...props}>
                <Box>
                  {market.name}

                  {!!market.description && (
                    <Typography display="block" color="grey.400" variant="caption" lineHeight={1} fontWeight={400}>
                      {market.description}
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
          />
          {watch('market.description') ? (
            <Typography display="block" color="grey.400" variant="caption" lineHeight={1} fontWeight={400}>
              {watch('market.description')}
            </Typography>
          ) : null}
        </Box>
        {translations}
        <Select<PrizeType, PrizeEditFieldsType>
          control={control}
          name="type"
          disabled
          label={t('prize.type')}
          elements={prizeTypeMock}
          renderFunction={type => (
            <MenuItem key={type.value} value={type.value}>
              {type.label}
            </MenuItem>
          )}
        />
        {watch('market.flow') !== undefined && watch('market.flow') === 'gwp' ? (
          <Select<PeriodType, PrizeEditFieldsType>
            control={control}
            name="period"
            disabled
            label={t('prize.period')}
            elements={periodsMock}
            renderFunction={type => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            )}
          />
        ) : null}
        <Box px={1} my={1}>
          <Typography>
            <strong>{t('prize.available')}:</strong> {prizeData.amountLeft}
          </Typography>
          {percent !== '' ? (
            <Typography color={percent === '50%' ? 'warning.dark' : 'error'}>
              <strong>{t('prize.lessThenPercentAvailable', { percent })}</strong>
            </Typography>
          ) : null}
        </Box>
        <Input<PrizeEditFieldsType> name="totalAmount" label={t('prize.periodTotal')} control={control} />
        {prizeData.type.includes('digital') ? (
          <FileUpload<PrizeEditFieldsType>
            extensions={excelExt}
            type="file"
            name="file"
            label={t('prize.file')}
            control={control}
          />
        ) : null}
        {/* {periods?.map((period, idx) => (
          <Box key={period.id} display="flex" flexDirection="column" gap={1} my={0.5}>
            <Typography>
              {t('prize.range')} {idx + 1}
            </Typography>
            <Box display="flex" gap={1}>
              <Input<PrizeEditFieldsType>
                control={control}
                type="date"
                wrapperProps={{ width: '100%', maxWidth: 180 }}
                dateInputProps={{ format: 'dd/MM/yyyy' }}
                name={`periods.${idx}.startDate`}
                label="Start date"
              />
              <Input<PrizeEditFieldsType>
                control={control}
                type="date"
                wrapperProps={{ width: '100%', maxWidth: 180 }}
                dateInputProps={{ format: 'dd/MM/yyyy' }}
                name={`periods.${idx}.endDate`}
                label="End date"
              />
              <Input<PrizeEditFieldsType>
                control={control}
                textInputProps={{ type: 'number' }}
                name={`periods.${idx}.amount`}
                label="Amount"
              />
              <Input<PrizeEditFieldsType>
                control={control}
                textInputProps={{
                  type: 'number',
                  disabled: true,
                  value: +watch(`periods.${idx}.amount`) - period.used,
                }}
                name={`periods.${idx}.amountLeft`}
                label="Amount left"
              />
              <IconButton
                sx={{ alignSelf: 'center' }}
                aria-label="remove range"
                color="error"
                disabled={period.api}
                onClick={() => {
                  remove(idx)
                }}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Box>
          </Box>
        ))}
        {errors?.periods?.message !== undefined ? (
          <Typography whiteSpace="break-spaces" fontSize={12} color="error">
            <>{errors.periods.message}</>
          </Typography>
        ) : null}
        <Button
          onClick={() => {
            append({
              amount: '0',
              used: 0,
              startDate: new Date(prizeData.market.promoLiveDate),
              endDate: new Date(prizeData.market.promoLiveDate),
              amountLeft: '',
              api: false,
            })
          }}
          sx={{ width: 'max-content' }}
        >
          {t('common.addPeriod')}
        </Button> */}
      </Box>
      {typeFields}
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          onClick={() => {
            navigate('/prizes-groups')
          }}
          startIcon={<ChevronLeft />}
        >
          {t('common.backListText')}
        </Button>
        <Button type="submit" variant="contained">
          {t('common.change')}
        </Button>
      </Box>
      {isLoading && <PageLoader />}
    </form>
  )
}

export default PrizeEditFields
